import { LAYER } from '../layers';
import { CalendarDays } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Calendar } from '../calendar/Calendar';
import { calendarAge } from '../../lib/formatDate';
import { useFloatingPanel } from '../../hooks/useFloatingPanel';
import { TextField } from './TextField';

/**
 * Seven 36px cells and the grid's padding need only 276px, but the HEADER is what sets this:
 * at 320 the two labels and the chevrons did not fit and the chosen day clipped to
 * "Monday 2…", which is the one string in the panel a reader needs whole. Sized to the
 * header, not to the grid.
 *
 * **Re-measure it whenever the header gains something.** 380 was sized against a plain
 * `September 2026` label; replacing that with a month select and a year select pushed the day
 * back to "Wednesda…" — the same clip, from the same cause, one change later. Anything added to
 * that row costs width here.
 */
const PANEL_WIDTH = 520;

/**
 * A calendar date field — a `YYYY-MM-DD` value, typed or picked.
 *
 * **It is still a native `type="date"` input.** That is the whole design: the browser's
 * segmented editor is kept for typing, and only the *picker* becomes the product's
 * [`<Calendar>`](../calendar/Calendar.tsx). The alternative — a read-only box that can only
 * be filled from a grid — reads well in a screenshot and is unusable on the first field that
 * needed it, a **date of birth**: reaching 1974 through month chevrons is six hundred
 * clicks. `<Calendar>` has a year picker now — added for exactly this field — but typing
 * `09/11/1974` is still faster than any picker, and it works exactly as it
 * did.
 *
 * So the field owns two things and no more:
 *  - it **hides the browser's own picker button** (`.date-field` in `index.css`), or the
 *    control would offer two calendars that look nothing like each other;
 *  - it puts ours behind the `trailing` slot `<TextField>` already has.
 *
 * Everything else is `<TextField>`'s: the label, the required asterisk, the `<HelpTip>`
 * description with its `sr-only` twin, and the error below the control. A date field that
 * diverged from the shared input on any of those would be worse than an obviously bespoke
 * one.
 *
 * **`showAge` turns it into a date-and-age group**, for a date a reader judges by the age it
 * implies — a date of birth, and nothing else so far. The age is a **derived readout, not a
 * second field**: it has no state, no name, nothing posts it, and the server recomputes it from
 * the date rather than trusting a number a client sent. See the segment below for why it is not
 * an input.
 *
 * **`min` and `max` reach both pickers**, as the same two props: the input enforces them on a
 * typed value, and `<Calendar>` strikes the same days through *and* bounds its year list to
 * them. They used to be folded into a `isDateDisabled` predicate here, which disabled the right
 * days and told the calendar nothing about the range — so the year picker would have offered
 * 1890 on a field whose `min` was 1990. Handing them to only one surface is how a date the form
 * refuses stays reachable in the picker beside it.
 */
export function DateField({
  id,
  value,
  onChange,
  min,
  max,
  disabled,
  showAge = false,
  ...rest
}: {
  id: string;
  /** `YYYY-MM-DD`, or empty. */
  value: string;
  onChange: (next: string) => void;
  /** Earliest selectable calendar date, `YYYY-MM-DD`. */
  min?: string;
  /** Latest selectable calendar date, `YYYY-MM-DD`. */
  max?: string;
  disabled?: boolean;
  /** Shows the whole years the value implies, as a locked segment after the calendar button. */
  showAge?: boolean;
  label: string;
  description?: string;
  error?: string;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Derived during render, never an effect: it is a pure function of `value`, and holding it
  // in state would leave a stale age on screen for one paint after every keystroke.
  const age = showAge ? calendarAge(value) : null;

  const close = useCallback(() => setOpen(false), []);
  const { triggerRef, panelRef, rect } = useFloatingPanel(open, close);

  /*
    Focus moves into the grid when the panel opens, so a keyboard reader lands on the day the
    roving tabindex is holding rather than tabbing through the page to find it.

    `rect` is in the deps, and that is the whole reason this works. `useFloatingPanel`
    measures in a layout effect, so on the render where `open` flips true the rect is still
    null and the portal below has not been created — focusing then found nothing and silently
    did nothing. The panel exists on the render after, which is the one this fires on.
  */
  useEffect(() => {
    if (!open || !rect) return;
    panelRef.current?.querySelector<HTMLElement>('[role="grid"] [tabindex="0"]')?.focus();
  }, [open, rect, panelRef]);

  function commit(next: string) {
    onChange(next);
    setOpen(false);

    // Focus goes back to the button that opened the panel, never nowhere. Closing a dialog
    // and leaving focus on `<body>` drops a keyboard reader at the top of the document.
    toggleRef.current?.focus();
  }

  return (
    <div ref={triggerRef}>
      {/*
        `error` travels in {...rest}. <TextField> owns the deferral that keeps a
        required-but-empty field from opening red — see useDeferredError.
      */}
      <TextField
        {...rest}
        id={id}
        ref={inputRef}
        type="date"
        /*
          Mono by default, and not a call-site decision: a date is a machine value, and the
          product sets those in mono everywhere else. It also makes the segmented editor read
          as segments — proportional digits put ` 07/23/1985 ` at a different width per value,
          so the slashes wandered as the reader typed.
        */
        className="date-field font-mono"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        min={min}
        max={max}
        disabled={disabled}
        trailingWidth={showAge ? 'wide' : 'default'}
        trailing={
          <>
            <button
            type="button"
            ref={toggleRef}
            disabled={disabled}
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? `${id}-calendar` : undefined}
            aria-label="Choose from a calendar"
            className="
              rounded-control p-1 text-slate-400 transition
              hover:bg-slate-100 hover:text-slate-700
              disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent
            "
          >
              <CalendarDays className="h-4 w-4" aria-hidden="true" />
            </button>

            {/*
              A derived readout, and deliberately NOT an input.

              A disabled `<input>` here would be a dead control the reader can tab to and a
              value the form would have to carry, for a number that is a pure function of the
              field beside it. It is a `<span>`: out of the tab run by construction, with
              nothing to post and nothing to keep in step.

              The visible text is `aria-hidden` with an `sr-only` twin carrying the full
              phrasing — the same pairing the calendar's read-only day cells use, and for the
              same reason: ARIA does not guarantee `aria-label` on a generic element, so a
              bare one is free to be dropped and the segment announces a naked number.
            */}
            {showAge ? (
              <span
                /*
                  A FIXED width, sized to the widest value it can ever hold — `100 yrs`.

                  Left to its content the segment is a different width at 9, 31 and 100, so the
                  divider beside it moves as the reader types and the control changes shape
                  under them. Three digits is the real ceiling rather than a generous guess:
                  the oldest documented human age is 122, and a `max` of today already refuses
                  everything on the other side of it.

                  `justify-end` so the number sits against the field's edge and the digits line
                  up with the dash the empty state draws.
                */
                className="
                  ml-1 flex h-full w-[4.25rem] shrink-0 items-center justify-end gap-1
                  self-stretch border-l border-slate-300 pl-2 pr-1 text-xs text-slate-500
                "
              >
                {age === null ? (
                  // An em-dash, not a collapsed segment. A slot that appears only once the
                  // date is valid changes the control's shape while the reader is typing into
                  // it — and the dash is what the product prints for an absent value anyway.
                  <span className="font-mono" aria-hidden="true">
                    —
                  </span>
                ) : (
                  <>
                    <span className="font-mono tabular-nums" aria-hidden="true">
                      {age}
                    </span>
                    <span aria-hidden="true">{age === 1 ? 'yr' : 'yrs'}</span>
                    <span className="sr-only">{`Age ${age} ${age === 1 ? 'year' : 'years'}`}</span>
                  </>
                )}
              </span>
            ) : null}
          </>
        }
      />

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              id={`${id}-calendar`}
              role="dialog"
              aria-label={`${rest.label} calendar`}
              style={{
                position: 'fixed',
                top: rect.top,
                // The panel is wider than the field it hangs off, so it is the one place the
                // hook's `rect.width` is not used — and it has to be kept on screen itself,
                // since a field in the right-hand column of a two-column modal would
                // otherwise push it past the viewport edge.
                left: Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)),
                width: PANEL_WIDTH,
              }}
              className={LAYER.PANEL}
            >
              <Calendar
                value={value || null}
                onChange={commit}
                min={min}
                max={max}
                className="shadow-overlay"
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
